/**
 * The last list each live screen showed, kept for the session, so a screen
 * that mounts again (Lessons after a room, say) renders from what it had
 * rather than from nothing while its listeners start. A listener's next
 * snapshot replaces it.
 */
const store = new Map<string, unknown>();

export function recall<T>(key: string): T | undefined {
  return store.get(key) as T | undefined;
}

export function remember<T>(key: string, value: T): T {
  store.set(key, value);
  return value;
}

/** On sign-out: nothing of one account shows for the next. */
export function forgetAll(): void {
  store.clear();
}

/** How long before a failed live list listens again: 1, 2, 4, 8, 16, then 30 seconds. */
export function liveListRetryMs(attempt: number): number {
  return Math.min(30_000, 1000 * 2 ** Math.max(0, attempt));
}

/**
 * A listener that tries once more: an error starts it again, and a second
 * error is reported (onFailed), so a first read that fails (a fresh sign-in
 * on iPhone Safari, say) is retried rather than read as an empty list.
 * `subscribe` is an onSnapshot wrapper taking its data and error callbacks.
 */
export function subscribeRetryingOnce<T>(
  subscribe: (onData: (value: T) => void, onError: (err: unknown) => void) => () => void,
  onData: (value: T) => void,
  onFailed: (err: unknown) => void,
): () => void {
  let unsub: () => void = () => {};
  let stopped = false;
  let errors = 0;
  const start = () => {
    unsub = subscribe((value) => { if (!stopped) onData(value); }, (err) => {
      if (stopped) return;
      errors += 1;
      if (errors === 1) start();
      else onFailed(err);
    });
  };
  start();
  return () => { stopped = true; unsub(); };
}
